import { markRaw } from 'vue'
import { defineStore } from 'pinia'
import { Novu, type Notification } from '@novu/js'

import NotificationsService, {
  type NotificationsInboxSession,
} from '../services/notifications'
import { useAuthStore } from './auth'

interface NotificationsState {
  configured: boolean
  loading: boolean
  listLoading: boolean
  unreadCount: number
  items: Notification[]
  hasMore: boolean
  previewOpen: boolean
  unreadItems: Notification[]
  unreadHasMore: boolean
  unreadListLoading: boolean
  unreadListError: string | null
  error: string | null
  novu: Novu | null
  unsubscribeFns: Array<() => void>
}

function unwrapResult<T>(result: { data?: T; error?: { message?: string } }): T {
  if (result.error) {
    throw new Error(result.error.message || 'Novu request failed')
  }
  if (result.data === undefined) {
    throw new Error('Novu returned empty data')
  }
  return result.data
}

let initGeneration = 0
let initPromise: Promise<void> | null = null

export const useNotificationsStore = defineStore('notifications', {
  state: (): NotificationsState => ({
    configured: false,
    loading: false,
    listLoading: false,
    unreadCount: 0,
    items: [],
    hasMore: false,
    previewOpen: false,
    unreadItems: [],
    unreadHasMore: false,
    unreadListLoading: false,
    unreadListError: null,
    error: null,
    novu: null,
    unsubscribeFns: [],
  }),

  actions: {
    async init() {
      const authStore = useAuthStore()
      if (authStore.userType !== 'registered') return
      if (this.novu) return
      if (initPromise) return initPromise

      initPromise = this.initializeNovu().finally(() => {
        initPromise = null
      })
      return initPromise
    },

    async initializeNovu() {
      const generation = initGeneration
      this.loading = true
      this.error = null
      try {
        const response = await NotificationsService.getInboxSession()
        if (generation !== initGeneration) return

        const session = response.data as NotificationsInboxSession
        if (!session.configured) {
          this.configured = false
          return
        }

        this.configured = true
        this.novu = markRaw(
          new Novu({
            applicationIdentifier: session.applicationIdentifier,
            subscriberId: session.subscriberId,
            subscriberHash: session.subscriberHash,
          }),
        )
        this.bindRealtime()
        await this.refreshUnreadCount()
      } catch (err) {
        if (generation !== initGeneration) return
        this.error = err instanceof Error ? err.message : 'No se pudieron cargar las notificaciones'
        this.configured = false
      } finally {
        if (generation === initGeneration) {
          this.loading = false
        }
      }
    },

    bindRealtime() {
      if (!this.novu) return
      this.clearSubscriptions()
      const events = [
        'notifications.notification_received',
        'notifications.unread_count_changed',
        'notification.read.resolved',
        'notification.unread.resolved',
        'notifications.read_all.resolved',
        'notifications.archive_all.resolved',
      ] as const

      events.forEach((eventName) => {
        const unsubscribe = this.novu!.on(eventName as any, (payload: any) => {
          if (
            eventName === 'notifications.unread_count_changed'
            && payload?.result?.total != null
          ) {
            this.unreadCount = payload.result.total
          }
          void this.syncInbox()
        })
        this.unsubscribeFns.push(unsubscribe)
      })
    },

    clearSubscriptions() {
      this.unsubscribeFns.forEach((unsubscribe) => unsubscribe())
      this.unsubscribeFns = []
    },

    async refreshUnreadCount() {
      if (!this.novu) {
        this.unreadCount = 0
        return
      }
      try {
        const result = await this.novu.notifications.count({
          read: false,
          archived: false,
          snoozed: false,
        })
        const data = unwrapResult(result) as { count?: number }
        this.unreadCount = data.count ?? 0
      } catch {
        // Keep last known count if Novu is temporarily unavailable
      }
    },

    async syncInbox() {
      if (!this.novu) return
      await this.refreshUnreadCount()
      await this.loadList({ preserveUnreadCount: true })
    },

    openPreview() {
      this.previewOpen = true
    },

    closePreview() {
      this.previewOpen = false
    },

    async loadUnread() {
      const authStore = useAuthStore()
      if (authStore.userType !== 'registered') {
        this.unreadItems = []
        this.unreadHasMore = false
        return
      }
      if (!this.novu) {
        await this.init()
      }
      if (!this.novu) {
        this.unreadItems = []
        this.unreadHasMore = false
        return
      }
      this.unreadListLoading = true
      this.unreadListError = null
      try {
        const result = await this.novu.notifications.list({
          limit: 20,
          read: false,
          archived: false,
          snoozed: false,
          useCache: false,
        })
        const data = unwrapResult(result)
        this.unreadItems = data.notifications
        this.unreadHasMore = data.hasMore
      } catch (err) {
        this.unreadListError = err instanceof Error
          ? err.message
          : 'No se pudieron listar las notificaciones'
      } finally {
        this.unreadListLoading = false
      }
    },

    async loadList(options: { preserveUnreadCount?: boolean } = {}) {
      const authStore = useAuthStore()
      if (authStore.userType !== 'registered') {
        this.items = []
        return
      }
      if (!this.novu) {
        await this.init()
      }
      if (!this.novu) {
        this.items = []
        return
      }
      this.listLoading = true
      this.error = null
      try {
        const result = await this.novu.notifications.list({
          limit: 30,
          archived: false,
          snoozed: false,
          useCache: false,
        })
        const data = unwrapResult(result)
        this.items = data.notifications
        this.hasMore = data.hasMore
        if (!options.preserveUnreadCount) {
          await this.refreshUnreadCount()
        }
      } catch (err) {
        this.error = err instanceof Error ? err.message : 'No se pudieron listar las notificaciones'
      } finally {
        this.listLoading = false
      }
    },

    async markAsRead(notificationId: string) {
      if (!this.novu) return
      await this.novu.notifications.read({ notificationId })
      const item = this.items.find((notification) => notification.id === notificationId)
      if (item && !item.isRead) {
        // Notification instances from Novu are mostly readonly; reload list for consistency
        await this.loadList()
      } else {
        await this.refreshUnreadCount()
      }
    },

    async markAllAsRead() {
      if (!this.novu) return
      await this.novu.notifications.readAll()
      await this.loadList()
    },

    reset() {
      initGeneration += 1
      initPromise = null
      this.clearSubscriptions()
      this.novu = null
      this.configured = false
      this.loading = false
      this.listLoading = false
      this.unreadCount = 0
      this.items = []
      this.hasMore = false
      this.previewOpen = false
      this.unreadItems = []
      this.unreadHasMore = false
      this.unreadListLoading = false
      this.unreadListError = null
      this.error = null
    },
  },
})
